import React from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import "swiper/swiper-bundle.css";
import { Button } from "react-bootstrap";
import { LanguageSelector } from './dimension/LanguageSelector';
import { useLanguage } from './dimension/LanguageContext';

type OnboardingProps = {
    onComplete: () => void;
};

const SETTING_ON_BOARD_COMPLETED = 'onboardingCompleted';

export const useOnboardingState = () => {
    const [completed, setCompleted] = React.useState(false);

    React.useEffect(() => {
        const stored = window.localStorage.getItem(SETTING_ON_BOARD_COMPLETED);
        setCompleted(stored === "true");
    }, []);

    const complete = () => {
        window.localStorage.setItem(SETTING_ON_BOARD_COMPLETED, "true");
        setCompleted(true);
    };

    return [completed, complete] as const;
};

const Onboarding: React.FC<OnboardingProps> = ({ onComplete }) => {
    const swiperRef = React.useRef<any>(null);
    const langCtx = useLanguage();
    
    const slides = [
        {
            title: langCtx.translate("onboarding_slide1_title"),
            description: langCtx.translate("onboarding_slide1_description"),
            image: "/img/Slide_1.jpg",
        },
        {
            title: langCtx.translate("onboarding_slide2_title"),
            description: langCtx.translate("onboarding_slide2_description"),
            image: "/img/Slide_2.jpg",
        },
        {
            title: langCtx.translate("onboarding_slide3_title"),
            description: langCtx.translate("onboarding_slide3_description"),
            image: "/img/Slide_3.jpg",
        },
        {
            title: langCtx.translate("onboarding_slide4_title"),
            description: langCtx.translate("onboarding_slide4_description"),
            image: "/img/Slide_4.jpg",
        },
        {
            title: langCtx.translate("onboarding_slide5_title"),
            description: langCtx.translate("onboarding_slide5_description"),
            image: "/img/Slide_5.jpg",
        },
    ];
      
    const handleNext = () => {
        swiperRef.current?.slideNext();
    };

    const handleGetStarted = () => {
        onComplete();
    };

    return (
        <div className="position-relative vh-100">
            <div style={{ position: "absolute", top: "16px", right: "16px", zIndex: 10 }}>
                <LanguageSelector />
            </div>
            <Swiper
                modules={[Pagination]}
                pagination={{ clickable: true }}
                allowTouchMove={ false }
                onSwiper={(swiper) => (swiperRef.current = swiper)}
            >
            {slides.map((slide, index) => (
                <SwiperSlide key={index}>
                    <div className="mt-4 text-center p-4">
                        <img src={slide.image} alt="" className="img-fluid" 
                            style={{ maxHeight: "260px", objectFit: "contain", margin: "0 auto", display: "block", marginBottom: "20px" }} 
                        />
                        <h2 className="fw-bold mb-2">{slide.title}</h2>
                        <p className="text-muted">{slide.description}</p>

                        <div className="mt-4 d-flex">
                            <Button
                                variant={index < slides.length - 1 ? "primary" : "success"}
                                onClick={index < slides.length - 1 ? handleNext : handleGetStarted}
                                className="w-50 mx-auto"
                                style={{
                                    backgroundColor: '#001f4d',
                                    borderColor: '#001f4d',
                                    color: 'white',
                                }}
                            >{index < slides.length - 1 ? langCtx.translate('NEXT') : langCtx.translate('GET STARTED')} </Button>
                        </div>
                        {index < slides.length - 1 && (
                            <Button
                                variant="link"
                                onClick={handleGetStarted}
                                className="mt-2 text-muted fs-6 text-decoration-none"
                            >{langCtx.translate('Skip')}</Button>
                        )}
                    </div>
                </SwiperSlide>
            ))}
            </Swiper>
        </div>
    );
};

export default Onboarding;
